import * as React from "react";
import {Helmet} from "react-helmet";
import {useI18next, useTranslation} from "gatsby-plugin-react-i18next";
import {useLocation} from "@reach/router";
import {safeString, createSafeT} from "../../lib/safeTranslation";

/* global __BUILD_DATE__ */

const BsHelmet = (props) => {

    const {t} = useTranslation();
    const {languages, language} = useI18next();
    const buildDate = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : '2026-01-01';
    const location = useLocation();
    const pathname = location ? location.pathname : '';

    let pathnameWithoutLang = pathname;
    if (language !== 'en' && pathname.startsWith(`/${language}/`)) {
        pathnameWithoutLang = pathname.substring(language.length + 1);
    }

    // Canonical URL should be self-referential (point to the current language version)
    // This is required for proper hreflang implementation and SEO best practices
    const canonicalUrl = `https://www.chatlab.com${pathname}`;

    // Use published date if provided, otherwise use build date
    const dateToUse = props.publishedDate || buildDate;

    // Create safe translation function
    const safeT = createSafeT(t);

    // Ensure all props are safely converted to strings
    const safeTitle = safeString(props.title);
    const safeDesc = safeString(props.desc);
    const safeAuthor = safeString(props.author);

    // Ensure languages is always an array to prevent Symbol errors
    const safeLanguages = Array.isArray(languages) ? languages : ['en'];

    return <Helmet
        htmlAttributes={{
            lang: language,
        }}
    >
        <meta charSet="utf-8"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no"/>
        <meta name="description" content={safeDesc}/>
        <title>{safeTitle}</title>
        <link rel="canonical" href={canonicalUrl}/>
        <meta name="robots" content="index,follow"/>
        <meta property="og:url" content={canonicalUrl}/>
        <meta property="og:title" content={safeTitle + " | " + safeT("ChatLab - AI Chatbots for Customer Support and Sales")}/>
        <meta property="og:description" content={safeDesc}/>
        <meta property="og:image" content="https://www.chatlab.com/logo/og-image.png"/>
        {props.publishedDate && <meta property="article:published_time" content={props.publishedDate}/>}
        {props.publishedDate ? <meta property="og:type" content="article"/> : <meta property="og:type" content="website"/>}

        {/* LCP Optimization: Preload critical resources */}
        {/* Removed incorrect preload links - StaticImage handles hero image optimization automatically */}
        {/* Fonts are loaded via CSS @font-face and don't need manual preload */}
        {safeLanguages.map(lng => (
            <link key={lng} rel="alternate" hrefLang={lng} href={`https://www.chatlab.com${lng === "en" ? "" : ("/" + lng)}${pathnameWithoutLang}`}/>
        ))}
        <link rel="alternate" hrefLang={"x-default"} href={`https://www.chatlab.com${pathnameWithoutLang}`}/>
        <link rel="schema.DC" href="https://purl.org/dc/elements/1.1/"/>
        <meta name="DC.Title" content={safeTitle + " | " + safeT("ChatLab - AI Chatbots for Customer Support and Sales")}/>
        <meta name="DC.Creator" content="ChatLab.com"/>
        <meta name="DC.Type" content={props.publishedDate ? "text" : "software"}/>
        <meta name="DC.Date" content={dateToUse}/>
        <meta name="DC.Format" content="text/html"/>
        <meta name="DC.Language" content={language}/>

        <script dangerouslySetInnerHTML={{
            __html: `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');
            function loadRw(){var s=document.createElement('script');s.src='https://r.wdfl.co/rw.js';s.dataset.rewardful='f93535';document.head.appendChild(s)}
            if('requestIdleCallback' in window){requestIdleCallback(loadRw)}else{window.addEventListener('load',loadRw)}`
        }}></script>
        <meta name="twitter:card" content="summary_large_image"/>
        <meta name="twitter:title" content={`${safeTitle} | ${safeT("ChatLab - Smart AI Chatbot")}`}/>
        <meta name="twitter:description" content={safeDesc}/>
        <meta name="twitter:image" content="https://www.chatlab.com/logo/og-image.png"/>

        {/* Organization Schema */}
        <script type="application/ld+json">
            {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "ChatLab",
                "url": "https://www.chatlab.com",
                "logo": "https://www.chatlab.com/logo/chatlab-square.png",
                "description": "AI-powered customer service agents and chatbots for websites, trained on your data and connected to your systems",
                "foundingDate": "2023-05-01",
                "numberOfEmployees": {
                    "@type": "QuantitativeValue",
                    "minValue": 1,
                    "maxValue": 10
                },
                "sameAs": [
                    "https://www.facebook.com/profile.php?id=61576535702089",
                    "https://www.youtube.com/@ChatLabCom",
                    "https://www.linkedin.com/company/chatlab/",
                    "https://x.com/ChatLabCom"
                ],
                "contactPoint": {
                    "@type": "ContactPoint",
                    "email": "contact@chatlab.com",
                    "contactType": "Customer Service",
                    "areaServed": "Worldwide",
                    "availableLanguage": ["en", "pl", "de", "fr", "es", "it", "tr"]
                },
                "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "Zamknięta 10/1.5",
                    "addressLocality": "Kraków",
                    "postalCode": "30-554",
                    "addressCountry": "PL"
                }
            })}
        </script>

        {/* BreadcrumbList Schema */}
        {props.breadcrumbs && (
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": props.breadcrumbs.map((crumb, index) => ({
                        "@type": "ListItem",
                        "position": index + 1,
                        "name": crumb.name,
                        "item": `https://www.chatlab.com${crumb.path}`
                    }))
                })}
            </script>
        )}

        {/* WebSite Schema */}
        <script type="application/ld+json">
            {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "WebSite",
                "name": "ChatLab",
                "url": "https://www.chatlab.com",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target": {
                        "@type": "EntryPoint",
                        "urlTemplate": "https://www.chatlab.com/blog?search={search_term_string}"
                    },
                    "query-input": "required name=search_term_string"
                }
            })}
        </script>

        {/* BlogPosting Schema for blog articles */}
        {props.publishedDate && (
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BlogPosting",
                    "headline": safeTitle,
                    "description": safeDesc,
                    "datePublished": props.publishedDate,
                    "author": {
                        "@type": "Person",
                        "name": safeAuthor || "ChatLab Team"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "ChatLab",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://www.chatlab.com/logo/chatlab-square.png"
                        }
                    },
                    "url": canonicalUrl,
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": canonicalUrl
                    }
                })}
            </script>
        )}

        {props.children}

    </Helmet>
}

export default BsHelmet